import { notEmpty, removeAtMedia, theme } from "@product/scmp-sdk";
import { useAsync, useMountEffect } from "@react-hookz/web";
import { differenceInDays } from "date-fns";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";

import { acknowledgementGateArticleIds, article as articleData } from "~/data/article";
import { section as sectionData } from "~/data/section";
import { accountAtom } from "~/lib/account";
import { fetchAiEngineData } from "~/lib/ai-engine/apis";
import { AiEngineDataType } from "~/lib/ai-engine/enums";
import { sortArrayByReferenceOrder } from "~/lib/utils";
import type { articleRecommendationArticle$key } from "~/queries/__generated__/articleRecommendationArticle.graphql";
import type { articleRecommendationQuery } from "~/queries/__generated__/articleRecommendationQuery.graphql";

export const useArticleRecommendation = (article_: articleRecommendationArticle$key) => {
  const article = useFragment(
    graphql`
      fragment articleRecommendationArticle on Article {
        entityId
        moreOnThisArticles {
          entityId
          publishedDate
          sections {
            value {
              entityId
            }
          }
          types {
            value {
              entityId
            }
          }
          urlAlias
        }
      }
    `,
    article_,
  );

  const environment = useRelayEnvironment();
  const { user } = useAtomValue(accountAtom);

  const [{ result: articleRecommendation }, { execute: executeFetchRecommendedArticles }] =
    useAsync(
      async () => {
        const aiEngineData = await fetchAiEngineData({
          entityId: article.entityId,
          limit: 20,
          type: AiEngineDataType.MoreOnThisTopic,
          userUuid: user?.uuid,
        });
        const recommendedArticles = await fetchQuery<articleRecommendationQuery>(
          environment,
          graphql`
            query articleRecommendationQuery($entityIds: [String], $first: Int!) {
              articles(filter: { entityIds: $entityIds }, first: $first) {
                edges {
                  node {
                    entityId
                    sections {
                      value {
                        entityId
                      }
                    }
                    types {
                      value {
                        entityId
                      }
                    }
                  }
                }
              }
            }
          `,
          {
            entityIds: aiEngineData.data.results.map(result => result.id) ?? [],
            first: aiEngineData.data.results.length ?? 0,
          },
          { fetchPolicy: "store-or-network" },
        ).toPromise();

        const orderedRecommendedArticles = sortArrayByReferenceOrder({
          data: {
            array: recommendedArticles?.articles.edges ?? [],
            idGetter: item => item.node.entityId,
          },
          order: { array: aiEngineData.data.results, idGetter: item => item.id },
        });

        const firstFilteredMoreOnThisArticle = article.moreOnThisArticles?.find(article => {
          if (!notEmpty(article)) return false;

          const isValidUrl = () => !article.urlAlias.match("http")?.length;

          const isExcludedPublishedDate = () => {
            if (!article.publishedDate) return false;
            return differenceInDays(new Date(), new Date(article.publishedDate)) > 30;
          };

          const isExplainerType = () => {
            const mainArticleType = first(article.types);
            if (!mainArticleType?.value) return false;
            return articleData.types.explainer.entityId === first(mainArticleType.value)?.entityId;
          };

          return (
            isValidUrl() &&
            !isAcknowledgementRequiredArticle(article) &&
            (!isExcludedPublishedDate() || isExplainerType()) &&
            !isExcludedSection(article)
          );
        });

        const isMobileOnly = window.matchMedia(
          removeAtMedia(theme.breakpoints.only("mobile")),
        ).matches;
        const results = orderedRecommendedArticles.reduce<{
          infiniteScrollArticleIds: string[];
          moreOnThisTopicWidgetIds: string[];
        }>(
          (ids, { node: article }) => {
            if (
              ids.infiniteScrollArticleIds.includes(article.entityId) ||
              ids.moreOnThisTopicWidgetIds.includes(article.entityId)
            )
              return ids;

            if (
              ids.infiniteScrollArticleIds.length < (isMobileOnly ? 5 : 2) &&
              !isAcknowledgementRequiredArticle(article) &&
              !isExcludedSection(article)
            ) {
              ids.infiniteScrollArticleIds.push(article.entityId);
            } else {
              ids.moreOnThisTopicWidgetIds.push(article.entityId);
            }
            return ids;
          },
          {
            infiniteScrollArticleIds: firstFilteredMoreOnThisArticle
              ? [firstFilteredMoreOnThisArticle.entityId]
              : [],
            moreOnThisTopicWidgetIds: [],
          },
        );

        return {
          recommendationId: aiEngineData.data.rec_id,
          results,
        };

        function isExcludedSection(article: {
          readonly sections: ReadonlyArray<{
            readonly value: ReadonlyArray<{
              readonly entityId: string;
            } | null> | null;
          } | null> | null;
        }) {
          const mainSection = first(article.sections);
          if (!mainSection) return false;

          const excludedSectionEntityIds = new Set<string>([
            sectionData.photo.entityId,
            sectionData.video.entityId,
            sectionData.sport.racing.entityId,
          ]);

          return mainSection.value?.some(
            value => value && excludedSectionEntityIds.has(value.entityId),
          );
        }

        function isAcknowledgementRequiredArticle(article: { entityId: string }) {
          return acknowledgementGateArticleIds.has(article.entityId);
        }
      },
      {
        recommendationId: "",
        results: {
          infiniteScrollArticleIds: [],
          moreOnThisTopicWidgetIds: [],
        },
      },
    );

  useMountEffect(executeFetchRecommendedArticles);

  return { articleRecommendation };
};
